import { useEffect, useState } from 'react';
import { ref as storageRef, getBlob } from 'firebase/storage';
import { storage } from '@/lib/firebase';

interface SafeLogoImageProps {
  logoUrl: string;
  className?: string;
  alt?: string;
}

export function SafeLogoImage({ logoUrl, className, alt = "Business Logo" }: SafeLogoImageProps) {
  const [localUrl, setLocalUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    if (!logoUrl) {
      setLocalUrl('');
      setAspectRatio(null);
      return;
    }

    let active = true;

    const measureAndSetUrl = (url: string) => {
      const img = new Image();
      img.onload = () => {
        if (active) {
          if (img.naturalWidth && img.naturalHeight) {
            setAspectRatio(img.naturalWidth / img.naturalHeight);
          } else {
            setAspectRatio(1);
          }
          setLocalUrl(url);
        }
      };
      img.onerror = () => {
        if (active) {
          setLocalUrl(url);
          setAspectRatio(1);
        }
      };
      img.src = url;
    };

    // If it's already a local data/blob URL, just use it
    if (
      logoUrl.startsWith('data:') || 
      logoUrl.startsWith('blob:') || 
      logoUrl.startsWith('http://localhost') || 
      logoUrl.startsWith('https://localhost')
    ) {
      measureAndSetUrl(logoUrl);
      return;
    }

    // If it's a firebase storage URL, load it using getBlob
    if (logoUrl.includes('firebasestorage.googleapis.com')) {
      const fetchLogo = async () => {
        setLoading(true);
        try {
          const fileRef = storageRef(storage, logoUrl);
          const blob = await getBlob(fileRef);
          if (active) {
            const url = URL.createObjectURL(blob);
            measureAndSetUrl(url);
          }
        } catch (error) {
          console.error('Error fetching safe logo blob:', error);
          // Fallback to direct url if it fails
          if (active) {
            measureAndSetUrl(logoUrl);
          }
        } finally {
          if (active) setLoading(false);
        }
      };

      fetchLogo();
      return () => {
        active = false;
      };
    }

    // Default fallback
    measureAndSetUrl(logoUrl);

    return () => {
      active = false;
    };
  }, [logoUrl]);

  if (loading) {
    return (
      <div className="h-16 w-32 bg-slate-100/50 animate-pulse rounded-lg flex items-center justify-center">
        <span className="text-[10px] text-muted-foreground">Loading Logo...</span>
      </div>
    );
  }

  if (!localUrl) return null;

  // Bounding box size: max width 200px, max height 64px (h-16 is 64px).
  // We compute the exact dimensions within this box that preserves the aspect ratio.
  const MAX_W = 200;
  const MAX_H = 64;

  let width: number | undefined;
  let height: number | undefined;

  if (aspectRatio) {
    // If the image is wider than the aspect ratio of the bounding box (200 / 64 = 3.125)
    if (aspectRatio > (MAX_W / MAX_H)) {
      width = MAX_W;
      height = MAX_W / aspectRatio;
    } else {
      height = MAX_H;
      width = MAX_H * aspectRatio;
    }
  }

  const style: React.CSSProperties = (width && height)
    ? {
        width: `${width}px`,
        height: `${height}px`,
        minWidth: `${width}px`,
        minHeight: `${height}px`,
        display: 'block',
      }
    : {
        height: `${MAX_H}px`,
        width: 'auto',
        display: 'block',
      };

  return (
    <img
      src={localUrl}
      alt={alt}
      className={className}
      style={style}
    />
  );
}
